import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import SearchIcon from '@mui/icons-material/Search';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import DownloadIcon from '@mui/icons-material/Download';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useSearchParams } from 'react-router-dom';

import api from '@src/api/clients';
import {
  createZipSubmission,
  cancelZipSubmission,
  checkZipOverlap,
  exportZipCoverageCsv,
  fetchZipSubmissions,
  pauseTerritory,
} from '@src/api/actions/dashboard/zipSubmissions';
import { useAuth } from '@src/context';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';
import useParentCompanyAutocomplete from '@src/hooks/useParentCompanyAutocomplete';
import useLocalStorage from '@src/hooks/useLocalStorage';

const STATUS_CONFIG = {
  pending: { color: 'warning', label: 'Pending' },
  approved: { color: 'success', label: 'Approved' },
  rejected: { color: 'error', label: 'Rejected' },
  cancelled: { color: 'default', label: 'Cancelled' },
};

const WALKTHROUGH_STEPS = [
  {
    ref: 'addVerticalBtn',
    title: 'Add Verticals',
    description: 'Start by selecting which verticals (service types) you want to manage. These will appear as options in each territory.',
  },
  {
    ref: 'newTerritoryBtn',
    title: 'Create Territories',
    description: 'Create territories to organize your ZIP coverage by region. Each territory can have multiple verticals with their own ZIPs.',
  },
  {
    ref: 'statsBar',
    title: 'Coverage at a Glance',
    description: 'See your total territories, ZIPs, active verticals, and pending submissions in one place.',
  },
  {
    ref: 'zipSearch',
    title: 'Find a ZIP',
    description: 'Quickly search to find which territory and vertical a specific ZIP code belongs to.',
  },
  {
    ref: 'territoryCard',
    title: 'Manage Your Territories',
    description: 'Expand a territory to see its verticals and ZIPs. Use "Manage ZIPs" to add or remove ZIPs, or the dropdown to add a new vertical.',
  },
  {
    ref: 'historyTab',
    title: 'Submission History',
    description: 'Track all your past submissions here — see what was approved, rejected, or is still pending review.',
  },
];

function WalkthroughOverlay({ stepIndex, steps, stepRefs, onNext, onBack, onSkip }) {
  const step = steps[stepIndex];
  const ref = stepRefs[step?.ref];
  const el = ref?.current;
  const [rect, setRect] = useState(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  useEffect(() => {
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const timer = setTimeout(() => {
        setRect(el.getBoundingClientRect());
      }, 350);
      return () => clearTimeout(timer);
    }
  }, [el, stepIndex]);

  if (!step || !el || !rect) return null;

  const isLast = stepIndex === steps.length - 1;
  const tooltipHeight = 180;
  const spaceBelow = window.innerHeight - rect.bottom - 16;
  const placeAbove = spaceBelow < tooltipHeight && rect.top > tooltipHeight;
  const tooltipTop = placeAbove ? rect.top - tooltipHeight - 16 : rect.bottom + 16;

  return (
    <>
      <Box
        onClick={onSkip}
        sx={{
          position: 'fixed',
          inset: 0,
          bgcolor: 'rgba(0,0,0,0.45)',
          zIndex: 1300,
          cursor: 'pointer',
        }}
      />
      <Box
        sx={{
          position: 'fixed',
          top: rect.top - 8,
          left: rect.left - 8,
          width: rect.width + 16,
          height: rect.height + 16,
          borderRadius: 2,
          border: '2px solid',
          borderColor: 'primary.main',
          boxShadow: '0 0 0 9999px rgba(0,0,0,0.45)',
          zIndex: 1301,
          pointerEvents: 'none',
        }}
      />
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          top: tooltipTop,
          left: Math.max(16, Math.min(rect.left, window.innerWidth - 380)),
          width: 360,
          zIndex: 1302,
          p: 2.5,
          borderRadius: 2,
        }}
      >
        <Stack spacing={1.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1" fontWeight={600}>{step.title}</Typography>
            <Typography color="text.secondary" variant="caption">
              {stepIndex + 1} / {steps.length}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">{step.description}</Typography>
          <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Button size="small" variant="text" onClick={onSkip}>Skip tour</Button>
            <Stack direction="row" spacing={1}>
              {stepIndex > 0 && (
                <Button size="small" variant="text" onClick={onBack}>Back</Button>
              )}
              <Button size="small" variant="contained" onClick={onNext}>
                {isLast ? 'Done' : 'Next'}
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Paper>
    </>
  );
}

export default function ZipCoveragePage() {
  const [searchParams] = useSearchParams();
  const { state } = useAuth();
  const user = state?.user;
  const memberships = user?.client_memberships || [];
  const isStaff = Boolean(user?.is_staff || user?.is_superuser);

  // Detect parent-company-scoped membership
  const isParentScopeMember = !isStaff && memberships.length === 1
    && !memberships[0]?.client_id && Boolean(memberships[0]?.parent_company_id);
  const memberParentCompanyId = isParentScopeMember
    ? String(memberships[0].parent_company_id) : '';

  // Parent company filter (staff can pick any; parent-scope members are locked to theirs)
  const {
    options: parentCompanyOptions,
    findOptionByValue: findParentCompanyByValue,
  } = useParentCompanyAutocomplete({ skip: !isStaff && !isParentScopeMember });
  const [selectedParentCompanyId, setSelectedParentCompanyId] = useState(
    memberParentCompanyId || ''
  );

  // Client selection — filtered by parent company when set
  const {
    options: clientOptions,
    handleInputChange: handleClientSearch,
    findOptionByValue,
  } = useClientAutocomplete({
    parentCompanyId: selectedParentCompanyId || undefined,
  });

  const initialClientId = searchParams.get('client_id')
    || (memberships.length === 1 && memberships[0]?.client_id
      ? String(memberships[0].client_id)
      : '');
  const [selectedClientId, setSelectedClientId] = useState(initialClientId);

  // Walkthrough
  const [walkthroughSeen, setWalkthroughSeen] = useLocalStorage('zipCoverage.walkthroughSeen', false);
  const [walkthroughActive, setWalkthroughActive] = useState(false);
  const [walkthroughStep, setWalkthroughStep] = useState(0);
  const walkthroughRefs = {
    addVerticalBtn: useRef(null),
    newTerritoryBtn: useRef(null),
    statsBar: useRef(null),
    zipSearch: useRef(null),
    territoryCard: useRef(null),
    historyTab: useRef(null),
  };

  // Data
  const [territories, setTerritories] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [allVerticals, setAllVerticals] = useState([]);
  const [addedVerticalIds, setAddedVerticalIds] = useState(new Set());
  const [addVerticalDialogOpen, setAddVerticalDialogOpen] = useState(false);
  const [zipsByTerritoryVertical, setZipsByTerritoryVertical] = useState({});
  const [submissions, setSubmissions] = useState([]);
  const [rejectedSubmissions, setRejectedSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Tabs & History
  const [activeTab, setActiveTab] = useState(0);
  const [historySubmissions, setHistorySubmissions] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyFilter, setHistoryFilter] = useState('all');

  // ZIP Search
  const [zipSearchText, setZipSearchText] = useState('');

  // Territory creation
  const [newTerritoryDialogOpen, setNewTerritoryDialogOpen] = useState(false);
  const [newTerritoryName, setNewTerritoryName] = useState('');
  const [newTerritoryError, setNewTerritoryError] = useState('');
  const [newTerritorySaving, setNewTerritorySaving] = useState(false);

  // Territory rename
  const [renameTerritoryDialogOpen, setRenameTerritoryDialogOpen] = useState(false);
  const [renameTerritory, setRenameTerritory] = useState(null);
  const [renameTerritoryName, setRenameTerritoryName] = useState('');
  const [renameTerritoryError, setRenameTerritoryError] = useState('');
  const [renameTerritorySaving, setRenameTerritorySaving] = useState(false);

  // Territory delete
  const [deleteTerritoryDialogOpen, setDeleteTerritoryDialogOpen] = useState(false);
  const [deleteTerritory, setDeleteTerritory] = useState(null);
  const [deleteTerritoryError, setDeleteTerritoryError] = useState('');
  const [deleteTerritorySaving, setDeleteTerritorySaving] = useState(false);

  // Manage ZIPs dialog
  const [manageDialogOpen, setManageDialogOpen] = useState(false);
  const [manageTerritory, setManageTerritory] = useState(null);
  const [manageVertical, setManageVertical] = useState(null);
  const [manageZipText, setManageZipText] = useState('');
  const [manageRemoveIds, setManageRemoveIds] = useState([]);
  const [manageSubmitting, setManageSubmitting] = useState(false);
  const [manageError, setManageError] = useState('');
  const [manageValidation, setManageValidation] = useState(null);
  const [manageValidating, setManageValidating] = useState(false);
  const [submitterNotes, setSubmitterNotes] = useState('');
  const validateTimerRef = useRef(null);
  const fileInputRef = useRef(null);

  // Submission confirmation dialog
  const [confirmationDialog, setConfirmationDialog] = useState(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnack = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  // ─── Data Loading ──────────────────────────────────────────────────

  const loadTerritories = useCallback(async () => {
    if (!selectedClientId) { setTerritories([]); return; }
    try {
      const res = await api.request({
        url: `dashboard/client-territories/?client_id=${selectedClientId}`,
        method: 'GET',
      });
      setTerritories(res.data || []);
    } catch { setTerritories([]); }
  }, [selectedClientId]);

  const loadVerticals = useCallback(async () => {
    if (!selectedClientId) { setVerticals([]); return; }
    try {
      const res = await api.request({
        url: `dashboard/client-zips/vertical_options/?client_id=${selectedClientId}`,
        method: 'GET',
      });
      setVerticals(res.data || []);
    } catch { setVerticals([]); }
  }, [selectedClientId]);

  const loadAllVerticals = useCallback(async () => {
    try {
      const res = await api.request({
        url: 'dashboard/verticals/all-options/',
        method: 'GET',
      });
      setAllVerticals(res.data || []);
    } catch { setAllVerticals([]); }
  }, []);

  const loadZips = useCallback(async () => {
    if (!selectedClientId) { setZipsByTerritoryVertical({}); return; }
    setLoading(true);
    try {
      const grouped = {};
      let page = 1;
      let hasMore = true;
      while (hasMore) {
        const params = new URLSearchParams({
          client: selectedClientId, active: 'true', page, page_size: 100,
        });
        const res = await api.request({
          url: `dashboard/client-zips/?${params}`,
          method: 'GET',
        });
        const results = res.data?.results || [];
        results.forEach((czv) => {
          const key = `${czv.client_territory || 'none'}__${czv.vertical}`;
          if (!grouped[key]) grouped[key] = [];
          grouped[key].push(czv);
        });
        hasMore = !!res.data?.next;
        page += 1;
      }
      setZipsByTerritoryVertical(grouped);
    } catch { setZipsByTerritoryVertical({}); }
    finally { setLoading(false); }
  }, [selectedClientId]);

  const loadSubmissions = useCallback(async () => {
    if (!selectedClientId) { setSubmissions([]); return; }
    try {
      const res = await fetchZipSubmissions({ client_id: selectedClientId, status: 'pending' });
      setSubmissions(res.data?.results || []);
    } catch { setSubmissions([]); }
  }, [selectedClientId]);

  const loadRejectedSubmissions = useCallback(async () => {
    if (!selectedClientId) { setRejectedSubmissions([]); return; }
    try {
      const res = await fetchZipSubmissions({ client_id: selectedClientId, status: 'rejected' });
      setRejectedSubmissions((res.data?.results || []).slice(0, 5));
    } catch { setRejectedSubmissions([]); }
  }, [selectedClientId]);

  const loadHistory = useCallback(async (page = 1, statusFilter = 'all', append = false) => {
    if (!selectedClientId) { setHistorySubmissions([]); return; }
    setHistoryLoading(true);
    try {
      const params = { client_id: selectedClientId, page, page_size: 25 };
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await fetchZipSubmissions(params);
      const results = res.data?.results || [];
      setHistorySubmissions((prev) => append ? [...prev, ...results] : results);
      setHistoryHasMore(!!res.data?.next);
      setHistoryPage(page);
    } catch { if (!append) setHistorySubmissions([]); }
    finally { setHistoryLoading(false); }
  }, [selectedClientId]);

  useEffect(() => { loadAllVerticals(); }, [loadAllVerticals]);

  useEffect(() => {
    setAddedVerticalIds(new Set());
    setActiveTab(0);
    setZipSearchText('');
    setHistoryFilter('all');
    loadTerritories();
    loadVerticals();
    loadZips();
    loadSubmissions();
    loadRejectedSubmissions();
  }, [selectedClientId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === 1 && selectedClientId) {
      setHistoryPage(1);
      loadHistory(1, historyFilter);
    }
  }, [activeTab, selectedClientId, historyFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Walkthrough ─────────────────────────────────────────────────

  useEffect(() => {
    if (!walkthroughSeen && !loading && selectedClientId && !walkthroughActive) {
      const timer = setTimeout(() => {
        // Find first step with a mounted ref
        const firstValid = WALKTHROUGH_STEPS.findIndex(
          (s) => walkthroughRefs[s.ref]?.current
        );
        if (firstValid >= 0) {
          setWalkthroughStep(firstValid);
          setWalkthroughActive(true);
        }
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [walkthroughSeen, loading, selectedClientId, walkthroughActive]); // eslint-disable-line react-hooks/exhaustive-deps

  const endWalkthrough = useCallback(() => {
    setWalkthroughActive(false);
    setWalkthroughStep(0);
    setWalkthroughSeen(true);
  }, [setWalkthroughSeen]);

  const nextWalkthroughStep = useCallback(() => {
    let next = walkthroughStep + 1;
    // Skip steps whose ref element doesn't exist
    while (next < WALKTHROUGH_STEPS.length) {
      const ref = walkthroughRefs[WALKTHROUGH_STEPS[next].ref];
      if (ref?.current) break;
      next += 1;
    }
    if (next >= WALKTHROUGH_STEPS.length) {
      endWalkthrough();
    } else {
      setWalkthroughStep(next);
    }
  }, [endWalkthrough, walkthroughStep]); // eslint-disable-line react-hooks/exhaustive-deps

  const prevWalkthroughStep = useCallback(() => {
    let prev = walkthroughStep - 1;
    while (prev >= 0) {
      const ref = walkthroughRefs[WALKTHROUGH_STEPS[prev].ref];
      if (ref?.current) break;
      prev -= 1;
    }
    setWalkthroughStep(Math.max(0, prev));
  }, [walkthroughStep]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Derived Data ──────────────────────────────────────────────────

  const availableVerticals = useMemo(() => {
    const clientIds = new Set(verticals.map((v) => v.id));
    const extra = allVerticals.filter(
      (v) => addedVerticalIds.has(v.id) && !clientIds.has(v.id)
    );
    return [...verticals, ...extra];
  }, [verticals, allVerticals, addedVerticalIds]);

  const verticalById = useMemo(() => {
    const map = {};
    allVerticals.forEach((v) => { map[v.id] = v; });
    availableVerticals.forEach((v) => { map[v.id] = v; });
    return map;
  }, [allVerticals, availableVerticals]);

  const territoryVerticals = useMemo(() => {
    const map = {};
    territories.forEach((t) => { map[t.id] = new Set(); });
    Object.keys(zipsByTerritoryVertical).forEach((key) => {
      const [tId, vId] = key.split('__');
      if (map[tId]) map[tId].add(Number(vId));
    });
    return map;
  }, [territories, zipsByTerritoryVertical]);

  const pendingByTerritoryVertical = useMemo(() => {
    const map = {};
    submissions.forEach((sub) => {
      const key = `${sub.client_territory || 'none'}__${sub.vertical}`;
      if (!map[key]) map[key] = [];
      map[key].push(sub);
    });
    return map;
  }, [submissions]);

  const territoriesWithPending = useMemo(() => {
    const set = new Set();
    submissions.forEach((sub) => {
      if (sub.client_territory) set.add(sub.client_territory);
    });
    return set;
  }, [submissions]);

  const stats = useMemo(() => {
    const allZips = Object.values(zipsByTerritoryVertical);
    const totalZips = allZips.reduce((sum, arr) => sum + arr.length, 0);
    const totalPopulation = allZips
      .reduce((sum, arr) => sum + arr.reduce((s, z) => s + (z.population || 0), 0), 0);
    const activeVerticalIds = new Set();
    Object.keys(zipsByTerritoryVertical).forEach((key) => {
      activeVerticalIds.add(key.split('__')[1]);
    });
    return {
      territories: territories.length,
      totalZips,
      totalPopulation,
      verticals: activeVerticalIds.size,
      pending: submissions.length,
    };
  }, [territories, zipsByTerritoryVertical, submissions]);

  const zipSearchResults = useMemo(() => {
    const q = zipSearchText.trim();
    if (!q || q.length < 2) return null;
    const results = [];
    for (const [key, zips] of Object.entries(zipsByTerritoryVertical)) {
      const [tId, vId] = key.split('__');
      const matching = zips.filter((czv) => czv.zip.includes(q));
      if (matching.length > 0) {
        const territory = territories.find((t) => String(t.id) === tId);
        const vertical = verticalById[Number(vId)];
        results.push({
          territory: territory?.name || 'Unknown',
          vertical: vertical?.vertical || 'Unknown',
          zips: matching.map((czv) => czv.zip),
        });
      }
    }
    return results;
  }, [zipSearchText, zipsByTerritoryVertical, territories, verticalById]);

  // ─── Territory Creation ────────────────────────────────────────────

  const handleCreateTerritory = async () => {
    const name = newTerritoryName.trim();
    if (!name) {
      setNewTerritoryError('Territory name is required.');
      return;
    }
    const existing = territories.find(
      (t) => t.name.toLowerCase() === name.toLowerCase()
    );
    if (existing) {
      setNewTerritoryError(`Territory "${existing.name}" already exists.`);
      return;
    }
    setNewTerritorySaving(true);
    setNewTerritoryError('');
    try {
      await api.request({
        url: 'dashboard/client-territories/',
        method: 'POST',
        payload: { client: selectedClientId, name },
      });
      setNewTerritoryDialogOpen(false);
      setNewTerritoryName('');
      loadTerritories();
      showSnack(`Territory "${name}" created.`);
    } catch (err) {
      const detail = err?.response?.data?.name?.[0]
        || err?.response?.data?.detail
        || 'Failed to create territory.';
      setNewTerritoryError(String(detail));
    } finally {
      setNewTerritorySaving(false);
    }
  };

  // ─── Territory Rename ──────────────────────────────────────────────

  const handleOpenRename = (territory) => {
    setRenameTerritory(territory);
    setRenameTerritoryName(territory.name);
    setRenameTerritoryError('');
    setRenameTerritoryDialogOpen(true);
  };

  const handleRenameTerritory = async () => {
    const name = renameTerritoryName.trim();
    if (!name) {
      setRenameTerritoryError('Territory name is required.');
      return;
    }
    if (name === renameTerritory.name) {
      setRenameTerritoryDialogOpen(false);
      return;
    }
    setRenameTerritorySaving(true);
    setRenameTerritoryError('');
    try {
      await api.request({
        url: `dashboard/client-territories/${renameTerritory.id}/`,
        method: 'PATCH',
        payload: { name },
      });
      setRenameTerritoryDialogOpen(false);
      loadTerritories();
      showSnack(`Territory renamed to "${name}".`);
    } catch (err) {
      const detail = err?.response?.data?.name?.[0]
        || err?.response?.data?.detail
        || 'Failed to rename territory.';
      setRenameTerritoryError(String(detail));
    } finally {
      setRenameTerritorySaving(false);
    }
  };

  // ─── Territory Delete ──────────────────────────────────────────────

  const handleOpenDelete = (territory) => {
    setDeleteTerritory(territory);
    setDeleteTerritoryError('');
    setDeleteTerritoryDialogOpen(true);
  };

  const handleDeleteTerritory = async () => {
    setDeleteTerritorySaving(true);
    setDeleteTerritoryError('');
    try {
      await api.request({
        url: `dashboard/client-territories/${deleteTerritory.id}/`,
        method: 'DELETE',
      });
      setDeleteTerritoryDialogOpen(false);
      loadTerritories();
      loadZips();
      showSnack(`Territory "${deleteTerritory.name}" deleted.`);
    } catch (err) {
      const detail = err?.response?.data?.detail
        || err?.response?.data?.[0]
        || 'Failed to delete territory. It may still have active ZIPs.';
      setDeleteTerritoryError(String(detail));
    } finally {
      setDeleteTerritorySaving(false);
    }
  };

  // ─── Manage ZIPs ──────────────────────────────────────────────────

  const handleOpenManageDialog = (territory, vertical) => {
    setManageTerritory(territory);
    setManageVertical(vertical);
    setManageZipText('');
    setManageRemoveIds([]);
    setManageError('');
    setManageValidation(null);
    setManageValidating(false);
    setSubmitterNotes('');
    setManageDialogOpen(true);
  };

  const currentManagedZips = useMemo(() => {
    if (!manageTerritory || !manageVertical) return [];
    const key = `${manageTerritory.id}__${manageVertical.id}`;
    return zipsByTerritoryVertical[key] || [];
  }, [manageTerritory, manageVertical, zipsByTerritoryVertical]);

  // Live validation for the manage dialog textarea
  useEffect(() => {
    if (!manageDialogOpen) {
      setManageValidation(null);
      setManageValidating(false);
      return;
    }

    const parsed = manageZipText
      .split(/[\n,\t]+/)
      .map((z) => z.trim())
      .filter(Boolean);

    if (parsed.length === 0) {
      setManageValidation(null);
      return;
    }

    const unique = [...new Set(parsed)];
    const activeSet = new Set(currentManagedZips.map((czv) => czv.zip));
    const dupes = unique.filter((z) => activeSet.has(z));
    const newZips = unique.filter((z) => !activeSet.has(z));

    setManageValidation({
      total: unique.length,
      inputDupes: parsed.length - unique.length,
      dupes,
      newZips,
      unknown: [],
      conflicts: [],
      apiChecked: false,
    });

    if (validateTimerRef.current) clearTimeout(validateTimerRef.current);

    if (newZips.length > 0 && manageTerritory && manageVertical) {
      setManageValidating(true);
      validateTimerRef.current = setTimeout(async () => {
        try {
          const res = await checkZipOverlap({
            client_id: Number(selectedClientId),
            vertical_id: manageVertical.id,
            client_territory_id: manageTerritory.id,
            zip_codes: newZips,
          });
          setManageValidation((prev) => prev ? {
            ...prev,
            unknown: res.data?.unknown_zips || [],
            conflicts: res.data?.conflicts || [],
            zipDetails: res.data?.zip_details || {},
            apiChecked: true,
          } : prev);
        } catch {
          // API check failed silently — submit will catch it
        } finally {
          setManageValidating(false);
        }
      }, 800);
    } else {
      setManageValidating(false);
    }

    return () => {
      if (validateTimerRef.current) clearTimeout(validateTimerRef.current);
    };
  }, [manageZipText, manageDialogOpen, currentManagedZips, selectedClientId, manageTerritory, manageVertical]);

  // CSV file upload handler
  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result || '';
      // Parse CSV: extract anything that looks like a ZIP (digits, allow leading zeros)
      const zips = text
        .split(/[\n\r,\t;|]+/)
        .map((s) => s.replace(/["']/g, '').trim())
        .filter((s) => /^\d{3,10}$/.test(s));
      if (zips.length === 0) {
        setManageError('No valid ZIP codes found in the file.');
        return;
      }
      setManageZipText((prev) => {
        const existing = prev.trim();
        return existing ? `${existing}\n${zips.join('\n')}` : zips.join('\n');
      });
      showSnack(`${zips.length} ZIP codes loaded from file.`);
    };
    reader.readAsText(file);
    // Reset so same file can be re-uploaded
    e.target.value = '';
  };

  const handleToggleRemove = (zipId) => {
    setManageRemoveIds((prev) =>
      prev.includes(zipId) ? prev.filter((id) => id !== zipId) : [...prev, zipId]
    );
  };

  const handleSubmitZipChanges = async () => {
    const addZips = manageZipText
      .split(/[\n,\t]+/)
      .map((z) => z.trim())
      .filter(Boolean);
    const removeZips = currentManagedZips
      .filter((czv) => manageRemoveIds.includes(czv.id))
      .map((czv) => czv.zip);

    if (!addZips.length && !removeZips.length) {
      setManageError('Add ZIPs or select ZIPs to remove.');
      return;
    }

    if (addZips.length) {
      try {
        const overlapRes = await checkZipOverlap({
          client_id: Number(selectedClientId),
          vertical_id: manageVertical.id,
          client_territory_id: manageTerritory.id,
          zip_codes: addZips,
        });
        const data = overlapRes.data;
        if (data.unknown_zips?.length) {
          setManageError(`Unknown ZIP codes: ${data.unknown_zips.slice(0, 10).join(', ')}`);
          return;
        }
        if (data.has_conflicts) {
          const conflictList = data.conflicts
            .slice(0, 5)
            .map((c) => `${c.zip} (in ${c.current_territory})`)
            .join(', ');
          setManageError(`ZIP conflicts with other territories: ${conflictList}`);
          return;
        }
      } catch {
        setManageError('Failed to check for overlaps.');
        return;
      }
    }

    const items = [
      ...addZips.map((z) => ({ zip_raw: z, action: 'add' })),
      ...removeZips.map((z) => ({ zip_raw: z, action: 'remove' })),
    ];

    setManageSubmitting(true);
    setManageError('');
    try {
      await createZipSubmission({
        client_id: Number(selectedClientId),
        client_territory_id: manageTerritory.id,
        vertical_id: manageVertical.id,
        submitter_notes: submitterNotes.trim(),
        items,
      });
      setManageDialogOpen(false);
      setConfirmationDialog({
        territory: manageTerritory.name,
        vertical: manageVertical.vertical,
        addCount: addZips.length,
        removeCount: removeZips.length,
      });
      loadSubmissions();
    } catch (err) {
      const data = err?.response?.data || {};
      const detail = data.detail
        || (data.conflicting_zips?.length
          ? `Pending conflicts: ${data.conflicting_zips.slice(0, 10).join(', ')}`
          : null)
        || (data.invalid_zips?.length
          ? `Unknown ZIP codes: ${data.invalid_zips.slice(0, 20).join(', ')}`
          : null)
        || 'Failed to submit changes.';
      setManageError(String(detail));
    } finally {
      setManageSubmitting(false);
    }
  };

  // ─── Pause Territory ──────────────────────────────────────────────

  const handlePauseTerritory = async (territory, vertical = null) => {
    const label = vertical
      ? `${territory.name} / ${vertical.vertical}`
      : territory.name;
    if (!window.confirm(`Submit a pause request for ${label}? This will go to review.`)) {
      return;
    }
    try {
      const payload = {
        client_id: Number(selectedClientId),
        client_territory_id: territory.id,
      };
      if (vertical) payload.vertical_id = vertical.id;
      await pauseTerritory(payload);
      showSnack(`Pause request submitted for ${label}.`);
      loadSubmissions();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to submit pause request.';
      showSnack(detail, 'error');
    }
  };

  // ─── Cancel Submission ─────────────────────────────────────────────

  const handleCancelSubmission = async (submissionId) => {
    if (!window.confirm('Cancel this pending submission?')) return;
    try {
      await cancelZipSubmission(submissionId);
      showSnack('Submission cancelled.');
      loadSubmissions();
    } catch {
      showSnack('Failed to cancel submission.', 'error');
    }
  };

  // ─── Render ────────────────────────────────────────────────────────

  const showClientSelector = isStaff || memberships.length > 1 || isParentScopeMember;

  return (
    <Box p={3}>
      {/* ─── Header ──────────────────────────────────────────────────── */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">ZIP Coverage</Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            startIcon={<HelpOutlineIcon />}
            variant="text"
            onClick={() => { setWalkthroughStep(0); setWalkthroughActive(true); }}
          >
            Tour
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              loadTerritories();
              loadVerticals();
              loadZips();
              loadSubmissions();
              loadRejectedSubmissions();
            }}
            disabled={loading || !selectedClientId}
          >
            {loading ? <CircularProgress size={18} /> : 'Refresh'}
          </Button>
          <Button
            ref={walkthroughRefs.addVerticalBtn}
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => setAddVerticalDialogOpen(true)}
            disabled={!selectedClientId}
          >
            Add Vertical
          </Button>
          <Button
            ref={walkthroughRefs.newTerritoryBtn}
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setNewTerritoryName('');
              setNewTerritoryError('');
              setNewTerritoryDialogOpen(true);
            }}
            disabled={!selectedClientId}
          >
            New Territory
          </Button>
        </Stack>
      </Box>

      {showClientSelector && (
        <Stack direction="row" spacing={2} mb={2} alignItems="center" flexWrap="wrap">
          {(isStaff || isParentScopeMember) && parentCompanyOptions.length > 0 && (
            <Autocomplete
              options={parentCompanyOptions}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              value={findParentCompanyByValue(selectedParentCompanyId)}
              onChange={(_, val) => {
                setSelectedParentCompanyId(val ? String(val.value) : '');
                setSelectedClientId('');
              }}
              disabled={isParentScopeMember}
              renderInput={(params) => (
                <TextField {...params} label="Parent Company" size="small" sx={{ minWidth: 240 }} />
              )}
              sx={{ minWidth: 240 }}
            />
          )}
          <Autocomplete
            options={clientOptions}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(o, v) => o.value === v.value}
            value={findOptionByValue(selectedClientId)}
            onChange={(_, val) => setSelectedClientId(val ? String(val.value) : '')}
            onInputChange={handleClientSearch}
            renderInput={(params) => (
              <TextField {...params} label="Client" size="small" sx={{ minWidth: 280 }} />
            )}
            sx={{ minWidth: 280 }}
          />
        </Stack>
      )}

      {!selectedClientId && (
        <Alert severity="info">Select a client to view ZIP coverage.</Alert>
      )}

      {selectedClientId && loading && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      )}

      {/* ─── Quick Stats ──────────────────────────────────────────────── */}
      {selectedClientId && !loading && (
        <Box ref={walkthroughRefs.statsBar} display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
          <StatCard label="Territories" value={stats.territories} />
          <StatCard label="Total ZIPs" value={stats.totalZips.toLocaleString()} />
          {stats.totalPopulation > 0 && (
            <StatCard label="Total Population" value={stats.totalPopulation.toLocaleString()} />
          )}
          <StatCard label="Verticals" value={stats.verticals} />
          <StatCard
            label="Pending"
            value={stats.pending}
            highlight={stats.pending > 0}
          />
          {stats.totalZips > 0 && (
            <Tooltip title="Download ZIP coverage as CSV">
              <IconButton
                size="small"
                onClick={async () => {
                  try {
                    const res = await exportZipCoverageCsv(selectedClientId);
                    const blob = new Blob([res.data], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `zip_coverage_${selectedClientId}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                    showSnack('CSV downloaded.');
                  } catch {
                    showSnack('Failed to download CSV.', 'error');
                  }
                }}
              >
                <DownloadIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      {/* ─── ZIP Search ───────────────────────────────────────────────── */}
      {selectedClientId && !loading && stats.totalZips > 0 && (
        <Box ref={walkthroughRefs.zipSearch} mb={2}>
          <TextField
            size="small"
            placeholder="Search for a ZIP code..."
            value={zipSearchText}
            onChange={(e) => setZipSearchText(e.target.value)}
            sx={{ width: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          {zipSearchResults !== null && (
            <Paper variant="outlined" sx={{ mt: 1, p: 1.5, maxWidth: 500 }}>
              {zipSearchResults.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No matching ZIPs found.
                </Typography>
              ) : (
                <Stack spacing={1}>
                  {zipSearchResults.map((r, i) => (
                    <Box key={i}>
                      <Typography variant="body2" fontWeight={600}>
                        {r.territory} / {r.vertical}
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                        {r.zips.slice(0, 20).map((z) => (
                          <Chip key={z} label={z} size="small" variant="outlined" />
                        ))}
                        {r.zips.length > 20 && (
                          <Chip label={`+${r.zips.length - 20} more`} size="small" />
                        )}
                      </Box>
                    </Box>
                  ))}
                </Stack>
              )}
            </Paper>
          )}
        </Box>
      )}

      {/* ─── Tabs ─────────────────────────────────────────────────────── */}
      {selectedClientId && !loading && (
        <Tabs
          ref={walkthroughRefs.historyTab}
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Coverage" />
          <Tab label="History" />
        </Tabs>
      )}

      {/* ═══════════ TAB 0: Coverage ═══════════ */}
      {selectedClientId && !loading && activeTab === 0 && (
        <>
          {/* Guided empty state */}
          {territories.length === 0 && (
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Get started with ZIP Coverage
              </Typography>
              <Stack spacing={1.5} alignItems="center" sx={{ maxWidth: 400, mx: 'auto' }}>
                <StepRow number={1} text='Click "New Territory" to create your first territory' />
                <StepRow number={2} text='Click "Add Vertical" to select which verticals you serve' />
                <StepRow number={3} text="Add ZIP codes to each territory and vertical" />
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                All ZIP changes are reviewed before going live.
              </Typography>
            </Paper>
          )}

          {/* Pending Submissions Banner */}
          {submissions.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {submissions.length} pending submission{submissions.length > 1 ? 's' : ''} awaiting review.
            </Alert>
          )}

          {/* Recently Rejected Submissions — with ZIP chips */}
          {rejectedSubmissions.length > 0 && (
            <Box mb={2}>
              {rejectedSubmissions.map((sub) => {
                const adds = (sub.items || []).filter((i) => i.action === 'add');
                const removes = (sub.items || []).filter((i) => i.action === 'remove');
                return (
                  <Alert key={sub.id} severity="error" sx={{ mb: 1 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                      Rejected: {sub.territory_name} / {sub.vertical_name}
                    </Typography>
                    {adds.length > 0 && (
                      <Box display="flex" flexWrap="wrap" gap={0.5} alignItems="center" mb={0.5}>
                        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                          Adding:
                        </Typography>
                        {adds.slice(0, 20).map((item) => (
                          <Chip
                            key={item.id}
                            label={item.zip || item.zip_raw}
                            size="small"
                            color="error"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        ))}
                        {adds.length > 20 && (
                          <Chip label={`+${adds.length - 20} more`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                        )}
                      </Box>
                    )}
                    {removes.length > 0 && (
                      <Box display="flex" flexWrap="wrap" gap={0.5} alignItems="center" mb={0.5}>
                        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                          Removing:
                        </Typography>
                        {removes.slice(0, 20).map((item) => (
                          <Chip
                            key={item.id}
                            label={item.zip || item.zip_raw}
                            size="small"
                            color="error"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        ))}
                        {removes.length > 20 && (
                          <Chip label={`+${removes.length - 20} more`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                        )}
                      </Box>
                    )}
                    {sub.reviewer_notes && (
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        Note: {sub.reviewer_notes}
                      </Typography>
                    )}
                  </Alert>
                );
              })}
            </Box>
          )}

          {/* Territory Cards */}
          {territories.map((territory, tIdx) => {
            const tVerticalIds = territoryVerticals[territory.id] || new Set();
            const pendingVerticalIds = new Set();
            submissions
              .filter((s) => s.client_territory === territory.id)
              .forEach((s) => pendingVerticalIds.add(s.vertical));
            const usedVerticalIds = new Set([...tVerticalIds, ...pendingVerticalIds]);
            const displayVerticals = [...usedVerticalIds]
              .map((vId) => verticalById[vId])
              .filter(Boolean);
            const hasPending = territoriesWithPending.has(territory.id);
            const territoryKeys = Object.keys(zipsByTerritoryVertical)
              .filter((k) => k.startsWith(`${territory.id}__`));
            const territoryZipCount = territoryKeys
              .reduce((sum, k) => sum + zipsByTerritoryVertical[k].length, 0);
            const territoryPopulation = territoryKeys
              .reduce((sum, k) => sum + zipsByTerritoryVertical[k].reduce((s, z) => s + (z.population || 0), 0), 0);

            return (
              <Accordion
                key={territory.id}
                ref={tIdx === 0 ? walkthroughRefs.territoryCard : undefined}
                defaultExpanded={territories.length <= 5}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center" gap={2} width="100%">
                    {hasPending && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: 'warning.main',
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <Typography variant="h6">{territory.name}</Typography>
                    {displayVerticals.length > 0 && (
                      <Chip
                        label={`${displayVerticals.length} vertical${displayVerticals.length > 1 ? 's' : ''}`}
                        size="small"
                        variant="outlined"
                      />
                    )}
                    {territoryZipCount > 0 && (
                      <Chip label={`${territoryZipCount} ZIPs`} size="small" color="primary" variant="outlined" />
                    )}
                    {territoryPopulation > 0 && (
                      <Chip label={`${territoryPopulation.toLocaleString()} pop.`} size="small" variant="outlined" />
                    )}
                    <Box flexGrow={1} />
                    <Tooltip title="Rename territory">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenRename(territory);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete territory">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenDelete(territory);
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Pause entire territory (all verticals)">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handlePauseTerritory(territory);
                        }}
                      >
                        <PauseCircleOutlineIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  {displayVerticals.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No ZIPs yet. Use the dropdown below to add ZIPs for a vertical.
                    </Typography>
                  )}

                  {/* Add ZIPs for a vertical not yet in this territory */}
                  {(() => {
                    const unusedVerticals = availableVerticals.filter((v) => !usedVerticalIds.has(v.id));
                    if (unusedVerticals.length === 0) return null;
                    return (
                      <Box mb={2}>
                        <Autocomplete
                          options={unusedVerticals}
                          getOptionLabel={(v) => v.vertical}
                          onChange={(_, val) => {
                            if (val) handleOpenManageDialog(territory, val);
                          }}
                          value={null}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Add ZIPs for a vertical..."
                              size="small"
                              sx={{ maxWidth: 300 }}
                            />
                          )}
                          blurOnSelect
                          clearOnBlur
                        />
                      </Box>
                    );
                  })()}

                  {displayVerticals.map((vertical) => {
                    const key = `${territory.id}__${vertical.id}`;
                    const zips = zipsByTerritoryVertical[key] || [];
                    const pendingSubs = pendingByTerritoryVertical[key] || [];

                    return (
                      <Paper key={vertical.id} variant="outlined" sx={{ p: 2, mb: 1.5 }}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography variant="subtitle1" fontWeight={600}>
                              {vertical.vertical}
                            </Typography>
                            <Chip
                              label={`${zips.length} active`}
                              size="small"
                              color="success"
                              variant="outlined"
                            />
                            {(() => {
                              const pop = zips.reduce((s, z) => s + (z.population || 0), 0);
                              return pop > 0 ? (
                                <Chip
                                  label={`${pop.toLocaleString()} pop.`}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontWeight: 500 }}
                                />
                              ) : null;
                            })()}
                            {pendingSubs.length > 0 && (
                              <Chip
                                label={`${pendingSubs.length} pending`}
                                size="small"
                                color="warning"
                              />
                            )}
                          </Stack>
                          <Stack direction="row" spacing={1}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleOpenManageDialog(territory, vertical)}
                            >
                              Manage ZIPs
                            </Button>
                            <Tooltip title="Pause this vertical">
                              <IconButton
                                size="small"
                                onClick={() => handlePauseTerritory(territory, vertical)}
                              >
                                <PauseCircleOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </Box>

                        {/* ZIP chips */}
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {zips.slice(0, 50).map((czv) => (
                            <Tooltip key={czv.id} title={czv.city && czv.state ? `${czv.city}, ${czv.state}` : ''} arrow>
                              <Chip label={czv.zip} size="small" variant="outlined" />
                            </Tooltip>
                          ))}
                          {zips.length > 50 && (
                            <Chip label={`+${zips.length - 50} more`} size="small" />
                          )}
                          {zips.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                              No active ZIPs
                            </Typography>
                          )}
                        </Box>

                        {/* Pending submissions for this territory+vertical */}
                        {pendingSubs.map((sub) => {
                          const adds = (sub.items || []).filter((i) => i.action === 'add');
                          const removes = (sub.items || []).filter((i) => i.action === 'remove');
                          return (
                            <Alert
                              key={sub.id}
                              severity="warning"
                              sx={{ mt: 1 }}
                              action={
                                <Button
                                  size="small"
                                  color="inherit"
                                  onClick={() => handleCancelSubmission(sub.id)}
                                >
                                  Cancel
                                </Button>
                              }
                            >
                              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                                Pending — submitted {new Date(sub.submitted_at).toLocaleDateString()}
                              </Typography>
                              {adds.length > 0 && (
                                <Box display="flex" flexWrap="wrap" gap={0.5} alignItems="center" mb={0.5}>
                                  <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                                    Adding:
                                  </Typography>
                                  {adds.slice(0, 30).map((item) => (
                                    <Chip
                                      key={item.id}
                                      label={item.zip || item.zip_raw}
                                      size="small"
                                      color="success"
                                      variant="outlined"
                                      sx={{ height: 20, fontSize: '0.7rem' }}
                                    />
                                  ))}
                                  {adds.length > 30 && (
                                    <Chip label={`+${adds.length - 30} more`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                                  )}
                                </Box>
                              )}
                              {removes.length > 0 && (
                                <Box display="flex" flexWrap="wrap" gap={0.5} alignItems="center">
                                  <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                                    Removing:
                                  </Typography>
                                  {removes.slice(0, 30).map((item) => (
                                    <Chip
                                      key={item.id}
                                      label={item.zip || item.zip_raw}
                                      size="small"
                                      color="error"
                                      variant="outlined"
                                      sx={{ height: 20, fontSize: '0.7rem' }}
                                    />
                                  ))}
                                  {removes.length > 30 && (
                                    <Chip label={`+${removes.length - 30} more`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                                  )}
                                </Box>
                              )}
                            </Alert>
                          );
                        })}
                      </Paper>
                    );
                  })}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </>
      )}

      {/* ═══════════ TAB 1: History ═══════════ */}
      {selectedClientId && !loading && activeTab === 1 && (
        <Box>
          {/* History filters */}
          <Box display="flex" gap={2} mb={2} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={historyFilter}
                label="Status"
                onChange={(e) => setHistoryFilter(e.target.value)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {historyLoading && historySubmissions.length === 0 && (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          )}

          {!historyLoading && historySubmissions.length === 0 && (
            <Alert severity="info">No submission history found.</Alert>
          )}

          {historySubmissions.length > 0 && (
            <Stack spacing={1.5}>
              {historySubmissions.map((sub) => {
                const cfg = STATUS_CONFIG[sub.status] || STATUS_CONFIG.pending;
                const adds = (sub.items || []).filter((i) => i.action === 'add');
                const removes = (sub.items || []).filter((i) => i.action === 'remove');
                return (
                  <Paper key={sub.id} variant="outlined" sx={{ p: 2 }}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="subtitle2">
                          {sub.territory_name || 'Unknown Territory'} / {sub.vertical_name || 'Unknown'}
                        </Typography>
                        <Chip label={cfg.label} size="small" color={cfg.color} />
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(sub.submitted_at).toLocaleDateString()}{' '}
                        {new Date(sub.submitted_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={2} mb={1}>
                      {sub.add_count > 0 && (
                        <Typography variant="body2" color="success.main">
                          +{sub.add_count} add{sub.add_count !== 1 ? 's' : ''}
                        </Typography>
                      )}
                      {sub.remove_count > 0 && (
                        <Typography variant="body2" color="error.main">
                          -{sub.remove_count} removal{sub.remove_count !== 1 ? 's' : ''}
                        </Typography>
                      )}
                    </Stack>

                    {adds.length > 0 && (
                      <Box display="flex" flexWrap="wrap" gap={0.5} mb={0.5}>
                        {adds.slice(0, 20).map((item) => (
                          <Chip
                            key={item.id}
                            label={item.zip || item.zip_raw}
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        ))}
                        {adds.length > 20 && (
                          <Chip label={`+${adds.length - 20} more`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                        )}
                      </Box>
                    )}
                    {removes.length > 0 && (
                      <Box display="flex" flexWrap="wrap" gap={0.5} mb={0.5}>
                        {removes.slice(0, 20).map((item) => (
                          <Chip
                            key={item.id}
                            label={item.zip || item.zip_raw}
                            size="small"
                            color="error"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        ))}
                        {removes.length > 20 && (
                          <Chip label={`+${removes.length - 20} more`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                        )}
                      </Box>
                    )}

                    {sub.reviewer_notes && (
                      <Alert severity={sub.status === 'rejected' ? 'error' : 'info'} sx={{ mt: 1 }} variant="outlined">
                        <Typography variant="body2">
                          <strong>Reviewer note:</strong> {sub.reviewer_notes}
                        </Typography>
                      </Alert>
                    )}

                    {sub.reviewed_at && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        Reviewed {new Date(sub.reviewed_at).toLocaleDateString()} by {sub.reviewed_by_email || 'staff'}
                      </Typography>
                    )}

                    {sub.status === 'pending' && (
                      <Box mt={1}>
                        <Button
                          size="small"
                          color="warning"
                          variant="outlined"
                          onClick={() => handleCancelSubmission(sub.id)}
                        >
                          Cancel Submission
                        </Button>
                      </Box>
                    )}
                  </Paper>
                );
              })}

              {/* Load more */}
              {historyHasMore && (
                <Box display="flex" justifyContent="center" mt={1}>
                  <Button
                    variant="outlined"
                    onClick={() => loadHistory(historyPage + 1, historyFilter, true)}
                    disabled={historyLoading}
                    startIcon={historyLoading ? <CircularProgress size={16} /> : null}
                  >
                    Load More
                  </Button>
                </Box>
              )}
            </Stack>
          )}
        </Box>
      )}

      {/* ─── Create Territory Dialog ──────────────────────────────────── */}
      <Dialog
        open={newTerritoryDialogOpen}
        onClose={() => setNewTerritoryDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Create Territory</DialogTitle>
        <DialogContent>
          {newTerritoryError && (
            <Alert severity="error" sx={{ mb: 2 }}>{newTerritoryError}</Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            label="Territory Name"
            value={newTerritoryName}
            onChange={(e) => setNewTerritoryName(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewTerritoryDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateTerritory}
            disabled={newTerritorySaving}
          >
            {newTerritorySaving ? <CircularProgress size={18} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Rename Territory Dialog ──────────────────────────────────── */}
      <Dialog
        open={renameTerritoryDialogOpen}
        onClose={() => setRenameTerritoryDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Rename Territory</DialogTitle>
        <DialogContent>
          {renameTerritoryError && (
            <Alert severity="error" sx={{ mb: 2 }}>{renameTerritoryError}</Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            label="New Name"
            value={renameTerritoryName}
            onChange={(e) => setRenameTerritoryName(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameTerritoryDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRenameTerritory}
            disabled={renameTerritorySaving}
          >
            {renameTerritorySaving ? <CircularProgress size={18} /> : 'Rename'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Delete Territory Dialog ──────────────────────────────────── */}
      <Dialog
        open={deleteTerritoryDialogOpen}
        onClose={() => setDeleteTerritoryDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Territory</DialogTitle>
        <DialogContent>
          {deleteTerritoryError && (
            <Alert severity="error" sx={{ mb: 2 }}>{deleteTerritoryError}</Alert>
          )}
          <Typography variant="body1">
            Are you sure you want to delete <strong>{deleteTerritory?.name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Territories with active ZIPs cannot be deleted. Remove all ZIPs first.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTerritoryDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteTerritory}
            disabled={deleteTerritorySaving}
          >
            {deleteTerritorySaving ? <CircularProgress size={18} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Manage ZIPs Dialog ───────────────────────────────────────── */}
      <Dialog
        open={manageDialogOpen}
        onClose={() => setManageDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Manage ZIPs — {manageTerritory?.name} / {manageVertical?.vertical}
        </DialogTitle>
        <DialogContent dividers>
          {manageError && (
            <Alert severity="error" sx={{ mb: 2 }}>{manageError}</Alert>
          )}

          <Typography variant="subtitle2" gutterBottom>
            Add ZIPs
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Paste ZIP codes below (comma, tab, or newline separated) or upload a CSV file.
          </Typography>

          <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={() => fileInputRef.current?.click()}
            >
              Upload CSV
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.txt,.tsv"
              style={{ display: 'none' }}
              onChange={handleFileUpload}
            />
          </Stack>

          <TextField
            multiline
            rows={4}
            fullWidth
            placeholder="10001, 10002, 10003..."
            value={manageZipText}
            onChange={(e) => setManageZipText(e.target.value)}
            sx={{ mb: 1 }}
          />

          {/* Live validation feedback */}
          {manageValidation && (
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'background.default' }}>
              <Stack spacing={0.75}>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <Chip
                    label={`${manageValidation.newZips.length} new`}
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                  {manageValidation.dupes.length > 0 && (
                    <Chip
                      label={`${manageValidation.dupes.length} already active (skipped)`}
                      size="small"
                      color="default"
                      variant="outlined"
                    />
                  )}
                  {manageValidation.inputDupes > 0 && (
                    <Chip
                      label={`${manageValidation.inputDupes} duplicate input${manageValidation.inputDupes > 1 ? 's' : ''}`}
                      size="small"
                      color="default"
                      variant="outlined"
                    />
                  )}
                  {manageValidating && (
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <CircularProgress size={14} />
                      <Typography variant="caption" color="text.secondary">
                        Checking...
                      </Typography>
                    </Stack>
                  )}
                </Box>

                {manageValidation.apiChecked && manageValidation.unknown.length > 0 && (
                  <Alert severity="error" variant="outlined" sx={{ py: 0.25 }}>
                    <Typography variant="caption">
                      Unknown ZIPs: {manageValidation.unknown.slice(0, 10).join(', ')}
                      {manageValidation.unknown.length > 10 ? ` +${manageValidation.unknown.length - 10} more` : ''}
                    </Typography>
                  </Alert>
                )}

                {manageValidation.apiChecked && manageValidation.conflicts.length > 0 && (
                  <Alert severity="warning" variant="outlined" sx={{ py: 0.25 }}>
                    <Typography variant="caption">
                      Conflicts: {manageValidation.conflicts.slice(0, 5).map((c) => `${c.zip} (${c.current_territory})`).join(', ')}
                      {manageValidation.conflicts.length > 5 ? ` +${manageValidation.conflicts.length - 5} more` : ''}
                    </Typography>
                  </Alert>
                )}

                {manageValidation.apiChecked
                  && manageValidation.unknown.length === 0
                  && manageValidation.conflicts.length === 0
                  && manageValidation.newZips.length > 0 && (
                  <>
                    <Typography variant="caption" color="success.main">
                      All ZIPs validated — ready to submit.
                    </Typography>
                    {manageValidation.zipDetails && Object.keys(manageValidation.zipDetails).length > 0 && (
                      <Box display="flex" flexWrap="wrap" gap={0.5} mt={0.5}>
                        {manageValidation.newZips.slice(0, 30).map((z) => {
                          const d = manageValidation.zipDetails[z];
                          return (
                            <Tooltip key={z} title={d?.city && d?.state ? `${d.city}, ${d.state}` : ''} arrow>
                              <Chip label={z} size="small" color="success" variant="outlined" />
                            </Tooltip>
                          );
                        })}
                        {manageValidation.newZips.length > 30 && (
                          <Chip label={`+${manageValidation.newZips.length - 30} more`} size="small" />
                        )}
                      </Box>
                    )}
                  </>
                )}
              </Stack>
            </Paper>
          )}

          <Divider sx={{ mb: 2 }} />

          <Typography variant="subtitle2" gutterBottom>
            Current ZIPs ({currentManagedZips.length})
            {manageRemoveIds.length > 0 && (
              <Chip
                label={`${manageRemoveIds.length} marked for removal`}
                size="small"
                color="error"
                sx={{ ml: 1 }}
              />
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Click a ZIP to toggle it for removal.
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={0.5} sx={{ maxHeight: 300, overflow: 'auto' }}>
            {currentManagedZips.map((czv) => {
              const isMarkedForRemoval = manageRemoveIds.includes(czv.id);
              return (
                <Tooltip key={czv.id} title={czv.city && czv.state ? `${czv.city}, ${czv.state}` : ''} arrow>
                  <Chip
                    label={czv.zip}
                    size="small"
                    color={isMarkedForRemoval ? 'error' : 'default'}
                    variant={isMarkedForRemoval ? 'filled' : 'outlined'}
                    onClick={() => handleToggleRemove(czv.id)}
                    sx={{ cursor: 'pointer' }}
                  />
                </Tooltip>
              );
            })}
          </Box>
          <TextField
            label="Note (optional)"
            placeholder="e.g. expanding to south FL, seasonal pause..."
            value={submitterNotes}
            onChange={(e) => setSubmitterNotes(e.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManageDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmitZipChanges}
            disabled={manageSubmitting}
            startIcon={manageSubmitting ? <CircularProgress size={16} /> : null}
          >
            Submit for Review
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Submission Confirmation Dialog ────────────────────────────── */}
      <Dialog
        open={!!confirmationDialog}
        onClose={() => setConfirmationDialog(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <CheckCircleOutlineIcon sx={{ fontSize: 56, color: 'success.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Submission Received
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your ZIP changes for <strong>{confirmationDialog?.territory} / {confirmationDialog?.vertical}</strong> have
            been submitted for review.
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            {confirmationDialog?.addCount > 0 && (
              <Chip
                label={`${confirmationDialog.addCount} ZIP${confirmationDialog.addCount !== 1 ? 's' : ''} to add`}
                color="success"
                variant="outlined"
              />
            )}
            {confirmationDialog?.removeCount > 0 && (
              <Chip
                label={`${confirmationDialog.removeCount} ZIP${confirmationDialog.removeCount !== 1 ? 's' : ''} to remove`}
                color="error"
                variant="outlined"
              />
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            You will be notified once the changes are reviewed.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button variant="contained" onClick={() => setConfirmationDialog(null)}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Add Vertical Dialog ─────────────────────────────────────── */}
      <Dialog
        open={addVerticalDialogOpen}
        onClose={() => setAddVerticalDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add Verticals</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select verticals you want to manage ZIPs for. They will appear in the
            territory dropdowns below.
          </Typography>
          <Stack spacing={1} sx={{ maxHeight: 400, overflow: 'auto' }}>
            {allVerticals.map((v) => {
              const alreadyActive = verticals.some((av) => av.id === v.id);
              const isAdded = addedVerticalIds.has(v.id);
              return (
                <Box
                  key={v.id}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    px: 1.5,
                    py: 0.75,
                    borderRadius: 1,
                    bgcolor: alreadyActive || isAdded ? 'action.selected' : 'transparent',
                  }}
                >
                  <Typography variant="body2">{v.vertical}</Typography>
                  {alreadyActive ? (
                    <Chip label="Active" size="small" color="success" variant="outlined" />
                  ) : (
                    <Switch
                      size="small"
                      checked={isAdded}
                      onChange={() => {
                        setAddedVerticalIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(v.id)) {
                            next.delete(v.id);
                          } else {
                            next.add(v.id);
                          }
                          return next;
                        });
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddVerticalDialogOpen(false)}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* ─── Snackbar ─────────────────────────────────────────────────── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* ─── Walkthrough ──────────────────────────────────────────────── */}
      {walkthroughActive && (
        <WalkthroughOverlay
          stepIndex={walkthroughStep}
          steps={WALKTHROUGH_STEPS}
          stepRefs={walkthroughRefs}
          onNext={nextWalkthroughStep}
          onBack={prevWalkthroughStep}
          onSkip={endWalkthrough}
        />
      )}
    </Box>
  );
}

// ─── Helper Components ─────────────────────────────────────────────

function StatCard({ label, value, highlight = false }) {
  return (
    <Card
      variant="outlined"
      sx={{
        minWidth: 120,
        flex: '1 1 0',
        ...(highlight && {
          borderColor: 'warning.main',
          bgcolor: 'warning.50',
        }),
      }}
    >
      <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="h5" fontWeight={700}>
          {value}
        </Typography>
      </CardContent>
    </Card>
  );
}

function StepRow({ number, text }) {
  return (
    <Box display="flex" alignItems="center" gap={1.5} width="100%">
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '0.8rem',
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {number}
      </Box>
      <Typography variant="body2" textAlign="left">
        {text}
      </Typography>
    </Box>
  );
}
